import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { copyText } from '@/lib/copyText';
import { api } from '@/lib/api';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import type { AuditLog } from '@/types/auditLog';
import { JsonBlock } from './JsonBlock';

interface Props {
  id: number | null;
  onClose: () => void;
}

export function AuditLogDetailDialog({ id, onClose }: Props) {
  // Keyed by id so a stale entry is never shown while the next one loads.
  const [loaded, setLoaded] = useState<{ id: number; entry?: AuditLog; error?: string } | null>(null);

  useEffect(() => {
    if (id === null) return;
    api.auditLogs
      .get(id)
      .then((entry) => setLoaded({ id, entry }))
      .catch((err) => setLoaded({ id, error: err instanceof Error ? err.message : 'Failed to load entry' }));
  }, [id]);

  const current = loaded && loaded.id === id ? loaded : null;
  const entry = current?.entry ?? null;
  const error = current?.error ?? null;
  const { captureOpener, restoreFocus } = useDialogFocusRestore();

  return (
    <Dialog open={id !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        onOpenAutoFocus={captureOpener}
        onCloseAutoFocus={restoreFocus}
      >
        <DialogHeader>
          <DialogTitle>Audit entry #{id}</DialogTitle>
          <DialogDescription>Sensitive values are redacted before storage.</DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}
        {!entry && !error && <div className="text-sm text-muted-foreground">Loading…</div>}
        {entry && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground">Time:</span> {new Date(entry.created_at).toLocaleString()}</div>
              <div><span className="text-muted-foreground">Actor:</span> {entry.api_key_name || entry.actor_type}</div>
              <div><span className="text-muted-foreground">Action:</span> {entry.action}</div>
              <div>
                <span className="text-muted-foreground">Result:</span>{' '}
                <Badge variant={entry.success ? 'default' : 'destructive'}>{entry.status_code}</Badge>{' '}
                {entry.error_code}
              </div>
              <div className="col-span-2 font-mono text-xs">{entry.method} {entry.path}</div>
              <div><span className="text-muted-foreground">IP:</span> {entry.ip_address}</div>
              <div><span className="text-muted-foreground">Duration:</span> {entry.duration_ms} ms</div>
              <div className="col-span-2 flex items-center gap-2">
                <span className="text-muted-foreground">Request ID:</span>
                <code className="text-xs">{entry.request_id}</code>
                <Button size="sm" variant="outline" aria-label="Copy request ID" onClick={() => void copyText(entry.request_id)}>
                  Copy
                </Button>
              </div>
              {entry.truncated && <div className="col-span-2 text-amber-600">Payload truncated to 64KB.</div>}
            </div>
            <JsonBlock title="Request headers" value={entry.request_headers} />
            <JsonBlock title="Query" value={entry.query} />
            <JsonBlock title="Request body" value={entry.request_body} defaultOpen />
            <JsonBlock title="Response body" value={entry.response_body} defaultOpen />
            <JsonBlock title="Details" value={entry.details} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
